import React, {useState, useCallback, useEffect} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
	Paper,
	Box,
	TextField,
	InputAdornment,
	IconButton,
	Chip,
	Grid,
	Button,
	Collapse,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Typography,
} from '@material-ui/core';
import {
	Search,
	FilterList,
	Clear,
	DateRange,
	GetApp,
} from '@material-ui/icons';
import {useOrders} from '../context/OrdersContext';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
	filterPaper: {
		padding: theme.spacing(2.5),
		borderRadius: 12,
		boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
	},
	searchBox: {
		'& .MuiOutlinedInput-root': {
			borderRadius: 8,
			backgroundColor: '#fff',
		},
	},
	statusChip: {
		margin: theme.spacing(0.5),
		fontWeight: 600,
		cursor: 'pointer',
		transition: 'all 0.2s ease',
		'&:hover': {
			transform: 'translateY(-2px)',
		},
	},
	activeChip: {
		backgroundColor: theme.palette.primary.main,
		color: '#fff',
		'&:hover': {
			backgroundColor: theme.palette.primary.dark,
		},
	},
	filterButton: {
		borderRadius: 8,
		textTransform: 'none',
		fontWeight: 600,
	},
	dateField: {
		'& .MuiOutlinedInput-root': {
			borderRadius: 8,
		},
	},
	advancedFilters: {
		marginTop: theme.spacing(2),
		padding: theme.spacing(2),
		backgroundColor: '#f8f9fa',
		borderRadius: 8,
	},
}));

const statusOptions = [
	{label: 'All Status', value: '2345678', color: 'default', count: 'all'},
	{label: 'New', value: '2', color: 'primary', count: 'pending'},
	{label: 'Accepted', value: '3', color: 'secondary', count: 'processingNoDriver'},
	{label: 'Packed & On The Way', value: '45', color: 'default', count: 'packed_delivering'},
	{label: 'In Transit', value: '58', color: 'primary', count: 'inTransitCount'},
	{label: 'Delivered', value: '6', color: 'primary', count: 'delivered'},
	{label: 'Arrived', value: '8', color: 'default', count: 'arrived'},
	{label: 'Cancelled', value: '7', color: 'secondary', count: 'cancel'},
	{label: 'Waiting Payment', value: '-1', color: 'default', count: 'waitingPayment'},
	// pendingPaymentsCount, not unpaidCount: the latter also counts overpaid
	// orders, which the list never returns.
	{label: 'Pending Payments', value: 'pending-payments', color: 'secondary', count: 'pendingPaymentsCount', isSpecial: true},
	// Delivery-focused filters. Both follow the selected date range, so the chip
	// reads "For Delivery" rather than "For Delivery Today" -- the range decides
	// the window, not the label.
	{
		label: 'For Delivery',
		value: 'delivery-today',
		color: 'primary',
		count: 'forDeliveryCount',
		isSpecial: true
	},
	{label: 'Late Orders', value: 'late-orders', color: 'secondary', count: 'lateOrders', isSpecial: true},
];

const provinceOptions = [
	{label: 'All Locations', value: 'all', color: 'default'},
	{label: 'Phnom Penh', value: '0', color: 'primary'},
	{label: 'Province', value: '1', color: 'secondary'},
];

const OrdersFilters = () => {
	const classes = useStyles();
	const {state, actions} = useOrders();
	const {
		statusFilter,
		searchText,
		dateRange,
		basketTypeFilter,
		sourceFilter,
		provinceFilter,
		orderStatusCount,
	} = state;

	const [showAdvanced, setShowAdvanced] = useState(false);
	const [localSearch, setLocalSearch] = useState(searchText);

	// Follow searchText when it is changed outside this input (e.g. Refresh resets the filters).
	useEffect(() => {
		setLocalSearch(searchText);
	}, [searchText]);

	// Date presets
	const datePresets = [
		{
			label: 'All',
			key: 'all',
			start: null,
			end: null,
		},
		{
			label: 'Today',
			start: moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
			end: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
		},
		{
			label: 'Yesterday',
			start: moment().subtract(1, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
			end: moment().subtract(1, 'days').endOf('day').format('YYYY-MM-DD HH:mm:ss'),
		},
		{
			label: 'Last 7 Days',
			start: moment().subtract(7, 'days').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
			end: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
		},
		{
			label: 'This Month',
			start: moment().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
			end: moment().endOf('month').format('YYYY-MM-DD HH:mm:ss'),
		},
		{
			label: 'Last Month',
			start: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD HH:mm:ss'),
			end: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD HH:mm:ss'),
		},
	];

	// Handle search with debounce
	const handleSearchChange = useCallback((event) => {
		const value = event.target.value;
		setLocalSearch(value);
	}, []);

	const handleSearchSubmit = () => {
		actions.setSearchText(localSearch);
	};

	const handleSearchKeyPress = (event) => {
		if (event.key === 'Enter') {
			handleSearchSubmit();
		}
	};

	const handleClearSearch = () => {
		setLocalSearch('');
		actions.setSearchText('');
	};

	const handleStatusChange = (status) => {
		actions.setStatusFilter(status);
	};

	const handleDatePreset = (preset) => {
		if (preset.key === 'all') {
			// No date window: neither the range nor the backend's default fallback
			// is sent, so orders from every date are returned.
			actions.setDateRange({start: '', end: ''});
		} else {
			actions.setDateRange({start: preset.start, end: preset.end});
		}
	};

	const isDatePresetActive = (preset) => {
		if (preset.key === 'all') {
			return !dateRange.start && !dateRange.end;
		}
		return dateRange.start === preset.start && dateRange.end === preset.end;
	};

	const handleDateChange = (field, value) => {
		const formattedDate = moment(value).format('YYYY-MM-DD HH:mm:ss');
		actions.setDateRange({
			...dateRange,
			[field]: formattedDate,
		});
	};

	const handleClearFilters = () => {
		actions.resetFilters();
	};

	const activeFiltersCount = [
		searchText ? 1 : 0,
		statusFilter !== '2345678' ? 1 : 0,
		basketTypeFilter ? 1 : 0,
		sourceFilter ? 1 : 0,
		provinceFilter !== 'all' ? 1 : 0,
		(dateRange.start && dateRange.end) ? 1 : 0,
	].reduce((a, b) => a + b, 0);

	return (
		<Paper className={classes.filterPaper} elevation={0}>
			{/* Search Bar */}
			<Grid container spacing={2} alignItems="center">
				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						variant="outlined"
						placeholder="Search by Order ID, Customer Name, Phone, Email..."
						value={localSearch}
						onChange={handleSearchChange}
						onKeyPress={handleSearchKeyPress}
						className={classes.searchBox}
						size="small"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search color="action"/>
								</InputAdornment>
							),
							endAdornment: localSearch && (
								<InputAdornment position="end">
									<IconButton size="small" onClick={handleClearSearch}>
										<Clear/>
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
				</Grid>

				<Grid item xs={12} md={6}>
					<Box display="flex" gap={1} justifyContent="flex-end">
						<Button
							variant="outlined"
							startIcon={<FilterList/>}
							onClick={() => setShowAdvanced(!showAdvanced)}
							className={classes.filterButton}
						>
							Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
						</Button>

						{activeFiltersCount > 0 && (
							<Button
								variant="outlined"
								startIcon={<Clear/>}
								onClick={handleClearFilters}
								className={classes.filterButton}
							>
								Clear
							</Button>
						)}

						<Button
							variant="contained"
							color="primary"
							startIcon={<GetApp/>}
							onClick={actions.openExportDialog}
							className={classes.filterButton}
						>
							Export
						</Button>
					</Box>
				</Grid>
			</Grid>

			{/* Status Chips */}
			<Box mt={2}>
				{statusOptions.map((status) => {
					// Calculate count - for "Packed & On The Way" combine pickUp + delivering
					let count = 0;
					if (status.count === 'packed_delivering') {
						count = (orderStatusCount.pickUp || 0) + (orderStatusCount.delivering || 0);
					} else {
						count = orderStatusCount[status.count] || 0;
					}

					const isActive = statusFilter === status.value;

					return (
						<Chip
							key={status.value}
							label={`${status.label} (${count})`}
							onClick={() => handleStatusChange(status.value)}
							className={`${classes.statusChip} ${isActive ? classes.activeChip : ''}`}
							color={isActive ? 'primary' : 'default'}
							variant={isActive ? 'default' : 'outlined'}
						/>
					);
				})}
			</Box>

			{/* Province Filter Chips */}
			<Box mt={2}>
				<Typography variant="subtitle2" color="textSecondary" gutterBottom style={{fontWeight: 600, marginBottom: 8}}>
					Filter by Location:
				</Typography>
				{provinceOptions.map((province) => {
					let count = 0;
					if (province.value === 'all') {
						count = orderStatusCount.all || 0;
					} else if (province.value === '0') {
						// Phnom Penh
						count = orderStatusCount.ordersForDeliveryToday || 0;
					} else if (province.value === '1') {
						// Province
						count = orderStatusCount.provinceOrders || 0;
					}

					const isActive = provinceFilter === province.value;

					return (
						<Chip
							key={province.value}
							label={`${province.label} (${count})`}
							onClick={() => actions.setProvinceFilter(province.value)}
							className={`${classes.statusChip} ${isActive ? classes.activeChip : ''}`}
							color={isActive ? 'primary' : 'default'}
							variant={isActive ? 'default' : 'outlined'}
						/>
					);
				})}
			</Box>

			{/* Quick Date Filters - Always Visible */}
			<Box mt={2}>
				<Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
					<Chip
						icon={<DateRange/>}
						label="Quick Dates:"
						size="small"
						variant="outlined"
					/>
					{datePresets.map((preset) => {
						const isActive = isDatePresetActive(preset);
						return (
							<Chip
								key={preset.key || preset.label}
								label={preset.label}
								size="small"
								onClick={() => handleDatePreset(preset)}
								clickable
								color={isActive ? 'primary' : 'default'}
							/>
						);
					})}
				</Box>
			</Box>

			{/* Advanced Filters */}
			<Collapse in={showAdvanced}>
				<Box className={classes.advancedFilters}>
					<Grid container spacing={2}>
						{/* Date Range */}
						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								label="Start Date"
								type="datetime-local"
								variant="outlined"
								size="small"
								className={classes.dateField}
								value={dateRange.start ? moment(dateRange.start).format('YYYY-MM-DDTHH:mm') : ''}
								onChange={(e) => handleDateChange('start', e.target.value)}
								InputLabelProps={{shrink: true}}
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<TextField
								fullWidth
								label="End Date"
								type="datetime-local"
								variant="outlined"
								size="small"
								className={classes.dateField}
								value={dateRange.end ? moment(dateRange.end).format('YYYY-MM-DDTHH:mm') : ''}
								onChange={(e) => handleDateChange('end', e.target.value)}
								InputLabelProps={{shrink: true}}
							/>
						</Grid>

						{/* Basket Type Filter */}
						<Grid item xs={12} sm={6} md={3}>
							<FormControl fullWidth variant="outlined" size="small">
								<InputLabel>Basket Type</InputLabel>
								<Select
									value={basketTypeFilter}
									onChange={(e) => actions.setBasketTypeFilter(e.target.value)}
									label="Basket Type"
								>
									<MenuItem value="">All Type</MenuItem>
									<MenuItem value="0">Normal</MenuItem>
									<MenuItem value="1">Express</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						{/* Channel Filter — order_details.source */}
						<Grid item xs={12} sm={6} md={3}>
							<FormControl fullWidth variant="outlined" size="small">
								<InputLabel>Channel</InputLabel>
								<Select
									value={sourceFilter}
									onChange={(e) => actions.setSourceFilter(e.target.value)}
									label="Channel"
								>
									<MenuItem value="">All Channels</MenuItem>
									{/* "Delishop" is everything that is NOT Wing, so a future
									    channel still lands on one side of the filter. */}
									<MenuItem value="delishop">Delishop</MenuItem>
									<MenuItem value="wing">Wing Mini App</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						{/* Apply Button */}
						<Grid item xs={12} sm={6} md={3}>
							<Button
								fullWidth
								variant="contained"
								color="primary"
								onClick={handleSearchSubmit}
								className={classes.filterButton}
							>
								Apply Filters
							</Button>
						</Grid>

					</Grid>
				</Box>
			</Collapse>
		</Paper>
	);
};

export default OrdersFilters;
